import React, { FC } from "react";

import clsx from "clsx";
/* eslint-disable @typescript-eslint/no-unused-vars */
import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useLocalStorage } from "react-use";
import { Accordion, AccordionDetails, AccordionSummary, Button } from "@mui/material";

import {
    AccordionArrow,
    GreenCheckMark,
    MenuHelpIcon,
    MenuPersonIcon,
    MenuWorldIcon,
    MinusIcon,
    PlusThinIcon,
} from "@/assets/icons";
import { HiddenOnScope } from "@/components/atoms";
import config from "@/config";
import { sanitizeHtml, sanitizeUrl } from "@/core/security";
import { Permissions, UserState } from "@/shared/domain";
import { isABroker, useAuth, useNavigation } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import { StyledHamburgerMenuList } from "./styled";

const PanelIndex = {
    MY_ACCOUNT: 0,
    LANGUAGES: 1,
    HELP: 2,
};

type HamburgerMenuProps = {
    onClose: () => void;
};

const HamburgerMenu: FC<HamburgerMenuProps> = ({ onClose }) => {
    const { user, userState } = useAuth();
    const queryClient = useQueryClient();
    const [expanded, setExpanded] = React.useState(PanelIndex.MY_ACCOUNT);
    const { i18n, t } = useTranslation();
    const { logout } = useAuth();
    const { navToMyRequests, navToPolicyholders, navToDocuments, navToAddressChange, navToSettings } = useNavigation();
    const [, saveStoredLanguage] = useLocalStorage<string>("language", "", {
        raw: true,
    });
    const handleChange = (panel) => (_event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const onChangeLanguage = (theLanguage: string) => {
        onClose();
        i18n.changeLanguage(theLanguage); // Change language
        log.info("setLanguage", theLanguage);
        queryClient.clear();

        saveStoredLanguage(theLanguage);
    };

    const handleLogout = (isPreview: boolean) => {
        logout();
        tagManager.pushLogoutEvent();
        if (isPreview) {
            window.close();
        }
    };

    const getLogOutText = () => {
        if (isABroker()) return t("AccountUser.ClosePreview");
        if (user.isScopedLogin()) return t("AccountUser.LogoutScoped");
        return t("AccountUser.LogOut");
    };

    return (
        <>
            <StyledHamburgerMenuList>
                {user.isAuthenticated && (
                    <Accordion
                        classes={{ root: "accordion-root", expanded: "accordion-expanded" }}
                        expanded={expanded === PanelIndex.MY_ACCOUNT}
                        onChange={handleChange(PanelIndex.MY_ACCOUNT)}
                    >
                        <AccordionSummary
                            classes={{ root: "accordion-summary-root", content: "accordion-summary-content" }}
                            expandIcon={
                                expanded === PanelIndex.MY_ACCOUNT ? (
                                    <span className="minusIcon">
                                        <MinusIcon />
                                    </span>
                                ) : (
                                    <span className="plusIcon">
                                        <PlusThinIcon />
                                    </span>
                                )
                            }
                            aria-controls="panel1bh-content"
                            id="panel1bh-header"
                            data-testid="hamburger_menu-expand-account"
                        >
                            <div className="accordionInfo">
                                <MenuPersonIcon />
                                <span>{t("AccountUser.MyAccount")}</span>
                            </div>
                        </AccordionSummary>
                        <AccordionDetails classes={{ root: "accordion-details-root" }}>
                            {userState === UserState.ACCESS && (
                                <HiddenOnScope>
                                    {user.hasGlobalPermission(Permissions.ViewLinkedDocuments) && (
                                        <div
                                            className="details-wrapper"
                                            onClick={navToDocuments}
                                            data-testid="hamburger_menu-option-documents"
                                        >
                                            <span>{t("AccountUser.MyDocuments")}</span>
                                            <AccordionArrow />
                                        </div>
                                    )}
                                    <div
                                        className="details-wrapper"
                                        onClick={navToMyRequests}
                                        data-testid="hamburger_menu-option-requests"
                                    >
                                        <span>{t("Common.MyDemands")}</span>
                                        <AccordionArrow />
                                    </div>
                                    {user.hasGlobalPermission(Permissions.AccessChangeAddress) && (
                                        <div
                                            onClick={() => navToAddressChange(user.policyHolderId)}
                                            data-testid="hamburger_menu-option-address"
                                            className={clsx("details-wrapper", isABroker() && "disabled")}
                                            title={`${isABroker() ? t("Common.PreviewUserNotAllowed") : ""}`}
                                        >
                                            <span>{t("AccountUser.ChangeAddress")}</span>
                                            <AccordionArrow />
                                        </div>
                                    )}

                                    {user.hasGlobalPermission(Permissions.ViewInformation) && (
                                        <div
                                            className="details-wrapper"
                                            onClick={navToPolicyholders}
                                            data-testid="hamburger_menu-option-folders"
                                        >
                                            <span>{t("AccountUser.ManageFolders")}</span>
                                            <AccordionArrow />
                                        </div>
                                    )}
                                </HiddenOnScope>
                            )}
                            <div
                                className="details-wrapper"
                                onClick={navToSettings}
                                data-testid="hamburger_menu-option-settings"
                            >
                                <span>{t("AccountUser.Settings")}</span>
                                <AccordionArrow />
                            </div>
                        </AccordionDetails>
                    </Accordion>
                )}
                <Accordion
                    classes={{ root: "accordion-root", expanded: "accordion-expanded" }}
                    expanded={expanded === PanelIndex.LANGUAGES}
                    aria-controls="panel2bh-content"
                    id="panel2bh-header"
                    onChange={handleChange(PanelIndex.LANGUAGES)}
                >
                    <AccordionSummary
                        classes={{ root: "accordion-summary-root", content: "accordion-summary-content" }}
                        expandIcon={
                            expanded === PanelIndex.LANGUAGES ? (
                                <span className="minusIcon">
                                    <MinusIcon />
                                </span>
                            ) : (
                                <span className="plusIcon">
                                    <PlusThinIcon />
                                </span>
                            )
                        }
                        data-testid="hamburger_menu-expand-language"
                    >
                        <div className="accordionInfo">
                            <MenuWorldIcon />
                            <span>{t("Common.Languages")}</span>
                        </div>
                    </AccordionSummary>
                    <AccordionDetails classes={{ root: "accordion-details-root" }}>
                        <div
                            className="details-wrapper"
                            onClick={() => onChangeLanguage("nl")}
                            data-testid="hamburger_menu-option-nl"
                        >
                            <span>{t("LanguageSelector.dutch")}</span>
                            {i18n.language === "nl" && <GreenCheckMark className="checkMark" />}
                        </div>
                        <div
                            className="details-wrapper"
                            onClick={() => onChangeLanguage("en")}
                            data-testid="hamburger_menu-option-en"
                        >
                            <span>{t("LanguageSelector.english")}</span>
                            {i18n.language === "en" && <GreenCheckMark className="checkMark" />}
                        </div>
                        <div
                            className="details-wrapper"
                            onClick={() => onChangeLanguage("fr")}
                            data-testid="hamburger_menu-option-fr"
                        >
                            <span>{t("LanguageSelector.french")}</span>
                            {i18n.language === "fr" && <GreenCheckMark className="checkMark" />}
                        </div>
                        <div
                            className="details-wrapper"
                            onClick={() => onChangeLanguage("de")}
                            data-testid="hamburger_menu-option-de"
                        >
                            <span>{t("LanguageSelector.german")}</span>
                            {i18n.language === "de" && <GreenCheckMark className="checkMark" />}
                        </div>
                    </AccordionDetails>
                </Accordion>
                <Accordion
                    classes={{ root: "accordion-root", expanded: "accordion-expanded" }}
                    expanded={expanded === PanelIndex.HELP}
                    aria-controls="panel3bh-content"
                    id="panel3bh-header"
                    onChange={handleChange(PanelIndex.HELP)}
                >
                    <AccordionSummary
                        classes={{ root: "accordion-summary-root", content: "accordion-summary-content" }}
                        data-testid="hamburger_menu-expand-help"
                        expandIcon={
                            expanded === PanelIndex.HELP ? (
                                <span className="minusIcon">
                                    <MinusIcon />
                                </span>
                            ) : (
                                <span className="plusIcon">
                                    <PlusThinIcon />
                                </span>
                            )
                        }
                    >
                        <div className="accordionInfo">
                            <MenuHelpIcon />
                            <span>{t("Common.Help")}</span>
                        </div>
                    </AccordionSummary>
                    <AccordionDetails classes={{ root: "accordion-details-root" }}>
                        <div className="details-wrapper">
                            <span>
                                {t("NavBar.Help.HelpMessage")}{" "}
                                <a
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(t("NavBar.Help.HelpNumber")) }}
                                    href={`tel:${sanitizeUrl(config.HELP_PHONE_NUMBER)}`}
                                />
                            </span>
                        </div>
                    </AccordionDetails>
                </Accordion>

                {user.isAuthenticated && (
                    <Button
                        onClick={() => handleLogout(isABroker())}
                        className="mt20"
                        fullWidth
                        data-testid="hamburger_menu-button-logout"
                    >
                        {getLogOutText()}
                    </Button>
                )}
            </StyledHamburgerMenuList>
        </>
    );
};

export default HamburgerMenu;
